import { CommonModule } from '@angular/common';
import { Component, inject, TemplateRef, Type } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { HelpTier2Details, HelpTier3Video } from '../../models/help-content.model';
import { SafeUrlPipe } from '../../pipes/safe-url.pipe';

@Component({
  selector: 'slt-help-video-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, SafeUrlPipe],
  template: `
    <div class="video-dialog-header">
      <h3 mat-dialog-title>{{ data.title || 'Video Tutorial' }}</h3>
      <button mat-icon-button mat-dialog-close aria-label="Close video">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <mat-dialog-content class="video-content">
      @if (data.type === 'youtube') {
        <iframe
          [src]="data.videoUrl | safeUrl"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        ></iframe>
      } @else {
        <video controls autoplay [src]="data.videoUrl"></video>
      }
    </mat-dialog-content>
  `,
  styles: [
    `
      .video-dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 1rem 0 1rem;
        h3 {
          margin: 0;
        }
      }
      .video-content {
        padding: 0 !important;
        aspect-ratio: 16 / 9;
        overflow: hidden;
        background: #000;
        iframe,
        video {
          width: 100%;
          height: 100%;
          display: block;
        }
      }
    `,
  ],
})
export class HelpVideoDialogComponent {
  data: HelpTier3Video = inject(MAT_DIALOG_DATA);
}

@Component({
  selector: 'slt-help-detail-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="dialog-header">
      <div class="title-with-icon">
        <mat-icon color="primary">help_outline</mat-icon>
        <h2 mat-dialog-title>{{ data.title || 'Help & Details' }}</h2>
      </div>
      <button mat-icon-button mat-dialog-close aria-label="Close dialog">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <div class="content-body">
        <!-- 1. String / HTML Content -->
        @if (isStringContent(data.content)) {
          <div [innerHTML]="data.content"></div>
        }

        <!-- 2. ng-template Content -->
        @else if (isTemplateContent(data.content)) {
          <ng-container
            *ngTemplateOutlet="data.content; context: data.contentContext || {}"
          ></ng-container>
        }

        <!-- 3. Dynamic Component Content -->
        @else if (isComponentContent(data.content)) {
          <ng-container
            *ngComponentOutlet="data.content; inputs: data.contentContext || {}"
          ></ng-container>
        }
      </div>

      @if (data.keyPoints && data.keyPoints.length > 0) {
        <div class="key-points-box">
          <strong>Key Highlights:</strong>
          <ul>
            @for (point of data.keyPoints; track point) {
              <li>{{ point }}</li>
            }
          </ul>
        </div>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      @if (data.externalLink) {
        <a mat-button [href]="data.externalLink.url" target="_blank" rel="noopener noreferrer">
          <mat-icon>open_in_new</mat-icon>
          <span>{{ data.externalLink.label || 'Read Documentation' }}</span>
        </a>
      }

      @if (data.video) {
        <button mat-stroked-button color="primary" (click)="openVideo(data.video)">
          <mat-icon>play_circle_outline</mat-icon>
          <span>Watch Walkthrough</span>
        </button>
      }

      <div style="flex: 1;"></div>
      <button mat-flat-button mat-dialog-close>Got It</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75rem 1.25rem 0.25rem 1.25rem;
        .title-with-icon {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          h2 {
            margin: 0;
            font-size: 1.2rem;
          }
        }
      }
      .content-body {
        font-size: 0.95rem;
        line-height: 1.5;
        color: #333;
        margin-bottom: 1rem;
      }
      .key-points-box {
        background: #f8f9fa;
        border-left: 4px solid #1976d2;
        padding: 0.75rem 1rem;
        border-radius: 4px;
        font-size: 0.88rem;
        ul {
          margin: 0.4rem 0 0 1.2rem;
          padding: 0;
        }
        li {
          margin-bottom: 0.25rem;
        }
      }
    `,
  ],
})
export class HelpDetailDialogComponent {
  data: HelpTier2Details = inject(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);

  isStringContent(content: any): content is string {
    return typeof content === 'string';
  }

  isTemplateContent(content: any): content is TemplateRef<any> {
    return content instanceof TemplateRef;
  }

  isComponentContent(content: any): content is Type<any> {
    return typeof content === 'function';
  }

  openVideo(video: HelpTier3Video): void {
    this.dialog.open(HelpVideoDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: video,
    });
  }
}
